import { useEffect, useRef } from "react";

const focusable = ["a[href]", "button", "input", "select", "textarea", "summary", "[tabindex]"].join(",");
const openDialogs: HTMLElement[] = [];
const inertElements = new WeakMap<HTMLElement, { count: number; original: boolean }>();

function available(element: HTMLElement) {
  return (
    element.isConnected &&
    !element.matches(":disabled") &&
    !element.closest("[hidden], [inert]") &&
    element.getClientRects().length > 0 &&
    getComputedStyle(element).visibility !== "hidden"
  );
}

/** Keeps focus in the current modal, including when its form or preview changes. */
export function useDialogFocus<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose: () => void,
  fallbackFocus?: () => HTMLElement | null,
) {
  const dialogRef = useRef<T>(null);
  const closeRef = useRef(onClose);
  const fallbackRef = useRef(fallbackFocus);
  closeRef.current = onClose;
  fallbackRef.current = fallbackFocus;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const previous =
      document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null;
    openDialogs.push(dialog);
    const isCurrent = () => openDialogs.at(-1) === dialog;
    const items = () =>
      [...dialog.querySelectorAll<HTMLElement>(focusable)].filter((item) => item.tabIndex >= 0 && available(item));
    function focusFirst() {
      const preferred = dialog?.querySelector<HTMLElement>("[data-dialog-initial-focus]");
      (preferred && available(preferred) ? preferred : items()[0] || dialog)?.focus();
    }

    // Siblings along the ancestor path cover inline and portal dialogs without hiding the modal itself.
    const background: HTMLElement[] = [];
    let current: HTMLElement = dialog;
    while (current.parentElement) {
      for (const sibling of current.parentElement.children) {
        if (!(sibling instanceof HTMLElement) || sibling === current) continue;
        const state = inertElements.get(sibling) || { count: 0, original: sibling.inert };
        state.count++;
        inertElements.set(sibling, state);
        sibling.inert = true;
        background.push(sibling);
      }
      current = current.parentElement;
    }
    focusFirst();
    function keepFocus() {
      if (!isCurrent()) return;
      const active = document.activeElement;
      if (
        !(active instanceof HTMLElement) ||
        !dialog?.contains(active) ||
        !available(active) ||
        (active === dialog && items().length > 0)
      )
        focusFirst();
    }
    function keydown(event: KeyboardEvent) {
      if (!isCurrent()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = items();
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialog?.focus();
      } else if (!controls.includes(document.activeElement as HTMLElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    // Replacing a focused preview button otherwise leaves focus on <body>.
    const changes = new MutationObserver(keepFocus);
    changes.observe(dialog, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "hidden", "style", "class", "open"],
    });
    document.addEventListener("keydown", keydown);
    document.addEventListener("focusin", keepFocus);
    return () => {
      changes.disconnect();
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("focusin", keepFocus);
      openDialogs.splice(openDialogs.indexOf(dialog), 1);
      for (const element of background) {
        const state = inertElements.get(element);
        if (!state) continue;
        state.count--;
        if (!state.count) {
          element.inert = state.original;
          inertElements.delete(element);
        }
      }
      const target = previous && available(previous) ? previous : fallbackRef.current?.();
      if (target && available(target)) target.focus();
    };
  }, [open]);

  return dialogRef;
}
